import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ExternalLink, ChevronLeft, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ProductImage } from "@/components/ProductImage";
import { ProductCard } from "@/components/ProductCard";
import { StarRating, StarRatingDisplay } from "@/components/StarRating";
import { ShareMenu } from "@/components/ShareMenu";
import { ReviewDisclaimer } from "@/components/ReviewDisclaimer";
import { ReportButton } from "@/components/ReviewCard";
import { trackEvent } from "@/lib/analytics";
import { setPageMeta, injectJsonLd, removeJsonLd } from "@/lib/seo";
import { productSchema } from "@/lib/schema";

const CATEGORY_LABELS = {
  sonno: "Sonno & Routine serale",
  alimentazione: "Alimentazione selettiva",
  emozioni: "Emozioni, capricci & rabbia",
  autonomia: "Autonomia quotidiana",
  linguaggio: "Linguaggio & Prescrittura",
  "fratelli-amicizie": "Fratelli, gelosie & amicizie",
  scuola: "Scuola & passaggio Primaria",
  "gioco-outdoor": "Gioco, creatività & outdoor",
};

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [reviews, setReviews] = useState({ items: [], breakdown: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // review form
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: p }, { data: r }, { data: rel }] = await Promise.all([
        api.get(`/products/${slug}`),
        api.get(`/products/${slug}/reviews`),
        api.get(`/products/${slug}/related`),
      ]);
      setProduct(p);
      setReviews(r);
      setRelated(rel);
    } catch (e) {
      setError(e?.response?.status === 404 ? "not_found" : "error");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [slug]);

  // SEO meta + JSON-LD (invisibili all'utente)
  useEffect(() => {
    if (!product) return;
    const kindLabel = product.kind === "libro" ? "Libro" : product.kind === "gioco" ? "Gioco" : "App";
    const catLabel = CATEGORY_LABELS[product.category] || product.category;
    const stars = product.avg_rating ? ` ⭐ ${Number(product.avg_rating).toFixed(1)}/5` : "";
    const title = `${product.name}${product.brand_or_author ? " · " + product.brand_or_author : ""} — Per manina`;
    const description = `${kindLabel} educativo per bambini ${product.age_min}-${product.age_max} anni · ${catLabel}${stars}. ${product.description}`.slice(0, 250);
    setPageMeta({
      title,
      description,
      canonicalPath: `/prodotto/${product.slug}`,
      ogType: "product",
      ogImage: product.image_url || undefined,
    });
    injectJsonLd("schema-product", productSchema(product, reviews.items || []));
    return () => removeJsonLd("schema-product");
  }, [product, reviews]);

  const myReview = reviews.items.find((r) => r.user_id === user?.user_id);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) {
      sessionStorage.setItem("return_to", `/prodotto/${slug}`);
      const redirectUrl = window.location.origin + "/auth/callback";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }
    if (rating < 1) return toast.error("Seleziona da 1 a 5 stelle");
    if (comment.trim().length < 10) return toast.error("Il commento deve essere di almeno 10 caratteri");
    setSubmitting(true);
    try {
      await api.post(`/products/${slug}/reviews`, { rating, comment: comment.trim() });
      toast.success("Esperienza pubblicata!");
      trackEvent("review_submitted", { review_type: "product", product_slug: slug, rating });
      setRating(0); setComment("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nella pubblicazione");
    } finally {
      setSubmitting(false);
    }
  };

  const deleteMyReview = async () => {
    if (!myReview || !window.confirm("Eliminare la tua esperienza?")) return;
    try {
      await api.delete(`/product-reviews/${myReview.id}`);
      toast.success("Esperienza eliminata");
      load();
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  if (loading) return <div className="max-w-5xl mx-auto px-4 py-20 font-ui text-[#5C6B64]">Caricamento prodotto…</div>;
  if (error === "not_found") return (
    <div className="max-w-3xl mx-auto px-4 py-20 font-ui text-center">
      <p className="text-[#5C6B64]">Prodotto non trovato.</p>
      <Link to="/prodotti" className="inline-block mt-4 btn-primary">Esplora i prodotti</Link>
    </div>
  );
  if (!product) return null;

  const ageLabel = product.age_min === -1
    ? "GENITORI"
    : (product.age_min === product.age_max ? `${product.age_min} anni` : `${product.age_min}-${product.age_max} anni`);
  const total = reviews.total || 0;

  return (
    <div className="bg-[#FAF9F6]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <button onClick={() => navigate(`/prodotti/${product.category}`)}
          data-testid="back-to-category-link"
          className="inline-flex items-center gap-1 font-ui text-sm text-[#5C6B64] hover:text-[#C05C45]">
          <ChevronLeft size={16} /> {CATEGORY_LABELS[product.category] || "Categoria"}
        </button>
      </div>

      {/* HERO */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-10 grid grid-cols-1 md:grid-cols-[320px_1fr] gap-8 items-start">
        <ProductImage product={product} size="lg" className="w-full aspect-square rounded-2xl shadow-[0_8px_24px_-12px_rgba(26,54,45,0.2)]" />
        <div>
          <p className="label-eyebrow text-[#C05C45]">{product.kind === "libro" ? "Libro" : product.kind === "gioco" ? "Gioco educativo" : "App"}</p>
          <h1 data-testid="product-title" className="mt-2 font-display text-3xl sm:text-4xl text-[#1A362D] tracking-tight leading-tight">
            {product.name}
          </h1>
          {product.brand_or_author && (
            <p className="mt-2 font-ui text-base text-[#5C6B64]">{product.brand_or_author}</p>
          )}
          <div className="mt-4 flex flex-wrap items-center gap-3 text-sm font-ui">
            <span className="px-3 py-1 rounded-full bg-[#F2EFEB] text-[#1A362D]">{ageLabel}</span>
            <StarRatingDisplay value={product.avg_rating || 0} count={product.review_count || 0} />
          </div>

          <p className="mt-6 font-ui text-base text-[#1A362D] leading-relaxed">{product.description}</p>

          {/* Community attribution: "Proposto da [Nome]" */}
          {product.community_contributed && product.submitted_by_name && (
            <div className="mt-5 flex items-center gap-2 font-ui text-sm text-[#5C6B64]" data-testid="product-community-attribution">
              <span>Proposto da</span>
              {product.submitter_slug && user ? (
                <Link
                  to={`/utente/${product.submitter_slug}`}
                  data-testid="product-submitter-link"
                  className="inline-flex items-center gap-2 font-medium text-[#1A362D] hover:text-[#C05C45] transition-colors"
                >
                  {product.submitter_picture && (
                    <img
                      src={product.submitter_picture}
                      alt={product.submitted_by_name}
                      className="w-6 h-6 rounded-full object-cover border border-[#E8E6E1]"
                    />
                  )}
                  {product.submitted_by_name}
                </Link>
              ) : (
                <span className="inline-flex items-center gap-2 font-medium text-[#1A362D]">
                  {product.submitter_picture && (
                    <img
                      src={product.submitter_picture}
                      alt={product.submitted_by_name}
                      className="w-6 h-6 rounded-full object-cover border border-[#E8E6E1]"
                    />
                  )}
                  {product.submitted_by_name}
                </span>
              )}
            </div>
          )}

          {product.utility_note && (
            <div className="mt-5 bg-[#FFF8F0] border-l-4 border-[#C05C45] p-4 rounded-r-md">
              <p className="label-eyebrow text-[#C05C45]">Perché aiuta i genitori</p>
              <p className="mt-1 font-ui text-sm text-[#1A362D] leading-relaxed">{product.utility_note}</p>
            </div>
          )}

          {product.store_link && (
            <div className="mt-6 flex items-center gap-4 flex-wrap">
              <a
                href={product.store_link}
                target="_blank"
                rel="noopener noreferrer sponsored"
                data-testid="product-store-link"
                className="font-ui text-sm text-[#C05C45] hover:text-[#1A362D] underline underline-offset-2 inline-flex items-center gap-1.5 transition-colors"
              >
                Per saperne di più <ExternalLink size={13} />
              </a>
              <ShareMenu
                type="prodotto"
                slug={product.slug}
                title={`${product.name}${product.brand_or_author ? " · " + product.brand_or_author : ""}`}
                className="inline-flex items-center gap-1.5 text-sm font-ui text-[#5C6B64] hover:text-[#1A362D] transition-colors"
              />
            </div>
          )}
          {!product.store_link && (
            <div className="mt-6">
              <ShareMenu
                type="prodotto"
                slug={product.slug}
                title={`${product.name}${product.brand_or_author ? " · " + product.brand_or_author : ""}`}
                className="inline-flex items-center gap-1.5 text-sm font-ui text-[#5C6B64] hover:text-[#1A362D] transition-colors"
              />
            </div>
          )}
        </div>
      </section>

      {/* REVIEWS */}
      <section className="bg-[#F2EFEB] border-y border-[#E8E6E1]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="font-display text-2xl sm:text-3xl text-[#1A362D] tracking-tight">
            Esperienze dei genitori <span className="text-[#8A9691] font-normal text-xl">({total})</span>
          </h2>

          {/* Breakdown */}
          {total > 0 && (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6 items-start">
              <div className="bg-white border border-[#E8E6E1] rounded-xl p-5 text-center">
                <p className="font-display text-5xl text-[#1A362D]">{(product.avg_rating || 0).toFixed(1)}</p>
                <div className="mt-2"><StarRating value={product.avg_rating || 0} size={18} /></div>
                <p className="mt-2 font-ui text-xs text-[#8A9691]">su 5 — {total} {total === 1 ? "esperienza" : "esperienze"}</p>
              </div>
              <div className="space-y-1.5">
                {[5, 4, 3, 2, 1].map((n) => {
                  const c = reviews.breakdown?.[String(n)] || 0;
                  const pct = total ? (c / total) * 100 : 0;
                  return (
                    <div key={n} className="flex items-center gap-3 font-ui text-xs">
                      <span className="w-6 text-[#1A362D]">{n}★</span>
                      <div className="flex-1 h-2 bg-[#E8E6E1] rounded-full overflow-hidden">
                        <div className="h-full bg-[#E19A32]" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-10 text-right text-[#8A9691]">{c}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Form */}
          {!myReview && (
            <form onSubmit={submitReview} className="mt-10 bg-white border border-[#E8E6E1] rounded-xl p-6" data-testid="product-review-form">
              <p className="label-eyebrow text-[#C05C45]">La tua esperienza</p>
              <h3 className="mt-1 font-display text-xl text-[#1A362D]">Aiuta altri genitori — racconta</h3>
              <div className="mt-4">
                <p className="font-ui text-sm text-[#1A362D] mb-2">Valutazione</p>
                <StarRating value={rating} size={28} interactive onChange={setRating} testIdPrefix="product-review-star" />
              </div>
              <div className="mt-4">
                <label htmlFor="product-comment" className="font-ui text-sm text-[#1A362D]">Commento</label>
                <div className="mt-2"><ReviewDisclaimer /></div>
                <textarea
                  id="product-comment"
                  data-testid="product-review-comment"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={4}
                  maxLength={2000}
                  placeholder="Com'è stato utile per la tua famiglia? Per quale età/situazione?"
                  className="mt-1 w-full bg-white border border-[#E8E6E1] rounded-md p-3 font-ui text-sm text-[#1A362D] focus:outline-none focus:border-[#C05C45]"
                />
                <p className="mt-1 font-ui text-xs text-[#8A9691]">Minimo 10 caratteri.</p>
              </div>
              <button type="submit" disabled={submitting} data-testid="product-review-submit" className="mt-4 btn-primary disabled:opacity-50">
                {submitting ? "Pubblicazione…" : user ? "Racconta la tua esperienza" : "Accedi e racconta"}
              </button>
            </form>
          )}

          {/* List */}
          <div className="mt-8 space-y-4">
            {reviews.items.length === 0 ? (
              <p className="font-ui text-sm text-[#5C6B64] italic">Nessuna esperienza ancora. Sii il primo a condividere.</p>
            ) : (
              reviews.items.map((r) => (
                <article key={r.id} data-testid={`product-review-${r.id}`} className="bg-white border border-[#E8E6E1] rounded-xl p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      {r.user_picture ? (
                        <img src={r.user_picture} alt={r.user_name} className="w-9 h-9 rounded-full object-cover" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-[#F2EFEB] flex items-center justify-center font-ui text-sm text-[#1A362D]">
                          {(r.user_name || "?").slice(0, 1).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <p className="font-ui text-sm font-medium text-[#1A362D]">{r.user_name}</p>
                        <div className="mt-0.5"><StarRating value={r.rating} size={14} /></div>
                      </div>
                    </div>
                    {(r.user_id === user?.user_id || user?.is_admin) && (
                      <button onClick={deleteMyReview} title="Elimina" data-testid={`product-review-delete-${r.id}`} className="text-[#8A9691] hover:text-[#B33A3A]">
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                  <p className="mt-3 font-ui text-sm text-[#1A362D] leading-relaxed whitespace-pre-line">{r.comment}</p>
                  {r.user_id !== user?.user_id && <div className="mt-3"><ReportButton targetType="product_review" targetId={r.id} /></div>}
                </article>
              ))
            )}
          </div>
        </div>
      </section>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="font-display text-2xl text-[#1A362D] tracking-tight">Anche questi nella stessa area</h2>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-5">
            {related.map((p) => <ProductCard key={p.id} product={p} testIdPrefix="related-product" />)}
          </div>
        </section>
      )}
    </div>
  );
}
